import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import { sortBy, isArray, uniq, isEqual, isEmpty, cloneDeep } from 'lodash'
import { useTranslation } from 'react-i18next'

import {
	Grid,
	TextField,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	IconButton,
	Box,
	CircularProgress,
	Typography,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useGridApiRef, GridActionsCellItem } from '@mui/x-data-grid-premium'
import {
	Add as AddIcon,
	Delete as DeleteIcon,
	Close as CloseIcon,
	Edit as EditIcon,
	Visibility as VisibilityIcon,
	AccessTime as AccessTimeIcon,
} from '@mui/icons-material'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'

import { JobStatusCard } from './JobStatusCard'
import { TilePaginationDatagrid } from 'components/pages/dis-v2/_components/TileDatagrid'
import LoadingSpinner from 'custom-components/LoadingSpinner'
import DoformsRecordsActions from '../../../../data/datagrid/DoformsRecordsActions'

import { ENV_ACTIONS } from '../../../../../reducers/environmentReducer'

import {
	NOT_SUPPORT_ACTION_VIEW_TYPES,
	FEED_VIEW_TYPE,
	FEED_TYPES,
	PROJECT_FORM_VIEW_TYPE,
} from 'components/data/datagrid/CreatViewComponents/ViewDialogUtils'

import { useRecordQuery } from '../utils/tileRecordQuery'
import { useJobSummaryViewReducer } from './helper/reducers/jobViewReducer'
import { useJobActivityViewQuery } from './helper/reducers/jobAcitivityViewQuery'

import { off, onValue, ref } from 'firebase/database'
import database from '../../../../../firebase-config'

import { isNullOrUndefined, sleep } from 'utils/functions/helpers'
import { updateColumns } from '../../../../data/dataServices'
import { getSelectedGridRecord, parsedDataGridColumns } from '../../../../data/dataHelpers'
import { getFilteredRows } from '../../../../data/dataHelpers'
import {
	getDefaultOperator,
	getChildLinkedFieldsByTileKey,
} from '../../../../../utils/functions/helpers'
import DoFormsFormTile from '../doforms-form/DoformsFormTile'
import { DISPLAY_COLUMNS, COLUMNS_WITH_DEFAULT_PRESET_VALUE } from './helper/constant'
import { JobActivityGridComponent } from './JobActivityGridComponent'
import { hasDeletePermission, hasUpdatingPermission } from 'utils/functions/helpers'
import { JobActivityViewComponent } from './JobActivityViewComponent'

import { ModalDashboardLayoutContent } from '../../../dis/dashboard/Dashboard'
import { useModalDashboardDialog } from '../modal-dashboard/ModalDashboard'
import { mergeStateWithAggregationModel } from '@mui/x-data-grid-premium/hooks/features/aggregation/gridAggregationUtils'
import useProject from '../../hooks/useProject'

const useStyles = makeStyles(() => ({
	jobSummaryContainer: {
		display: 'flex',
		margin: '0 10px',
		width: '99%',
		height: '100%',
	},

	sideBarContainer: {
		display: 'flex',
		height: '100%',
		minWidth: '230px',
		justifyContent: 'center',
		alignItems: 'center',
		margin: '0 10px',
	},

	summaryContainer: {
		display: 'grid',
	},

	boxContain: {
		width: '100%',
		height: '100%',
		overflowY: 'auto',
	},

	contentContainer: {
		height: '100%',
		width: '100%',
		overflowY: 'hidden',
		overflowX: 'auto',
	},

	topNavContainer: {
		alignItems: 'center',
	},
}))

export function JobSummaryComponent(props) {
	const {
		tile,
		settings,
		dashboardKeyList,
		tileWrapperHeight,
		passedIdValue: parentPassedIdValue,
		modalDashboardSetting: parentModalDashboardSetting,
	} = props
	const { environment } = useSelector((state) => state)
	const permissions = environment?.userCurrent?.rights
	const classes = useStyles()
	const dispatch = useDispatch()
	const [t] = useTranslation()

	const apiRef = useGridApiRef()
	const sideBarContainerRef = useRef(null)

	const {
		tileWidth,
		selectedNewJobForm,
		selectedJobForm,
		selectedCustomerForm,
		selectedJobActivityForm,
		selectedNewTaskForm,
		columnsChecked,
		otherOptionsChecked,
		summaryWidgets,
		filterField,
		jobIdField,
		jobIdFieldCaption,
		statusField,
		progressBarField,
		showProgressBar = true, //default for this tile
		showPercentage = true, //default for this tile
		taskSummaryWidgets,
		taskStatusField,
		modalDashboardSetting: mainModalDashboardSetting,
	} = useMemo(() => settings, [settings])

	//lanscape dialog
	const { handleOpenModalDashboardDialog, handleCloseModalDashboardDialog, ModalDashboard } =
		useModalDashboardDialog({ parentModalDashboardSetting })

	// actions section
	const [openDialog, setOpenDialog] = useState(false)
	const [jobIdActionValue, setJobIdActionValue] = useState(null)
	const [populatedMessage, setPopulatedMessage] = useState([])
	const [activityJobFieldValue, setActivityJobFieldValue] = useState(null)

	function handleOpenDialog() {
		setOpenDialog(true)
	}

	function handleCloseDialog() {
		setActivityJobFieldValue(null)
		setJobIdActionValue(null)
		setPopulatedMessage([])
		setActionRecord(null)
		setOpenDialog(false)
	}

	const [action, setAction] = useState(null)
	const [actionRecord, setActionRecord] = useState(null)
	const [open, setOpen] = useState(false)
	const [error, setError] = useState(null)

	const jobStatusColors = useMemo(() => {
		if (isEmpty(summaryWidgets)) return []

		return summaryWidgets.map((widgetItem) => ({
			label: widgetItem.label,
			color: widgetItem.color,
			operator: widgetItem.operator,
			value: widgetItem.value,
		}))
	}, [summaryWidgets])

	const taskStatusColors = useMemo(() => {
		if (isEmpty(taskSummaryWidgets)) return []

		return taskSummaryWidgets.map((widgetItem) => ({
			label: widgetItem.label,
			color: widgetItem.color,
			operator: widgetItem.operator,
			value: widgetItem.value,
		}))
	}, [taskSummaryWidgets])

	const handleClose = () => {
		setAction(null)
		setActionRecord(null)
		setOpen(false)
	}

	// sidebar section
	const [selectedJobStatusInfo, setSelectedJobStatusInfo] = useState(null)

	const [selectedTaskStatusInfo, setSelectedTaskStatusInfo] = useState(null)

	//search section
	const [searchText, setSearchText] = useState(null)

	// selectedjob grid section
	const {
		viewData: jobViewData,
		allColumns: jobAllColumns,
		serverFilter: jobServerFilter,
		viewLoading: jobViewLoading,
	} = useJobSummaryViewReducer({
		formKey: selectedJobForm?.formKey,
		projectKey: selectedJobForm?.projectKey,
		columnsChecked,
		tile,
	})

	const [jobGridColumns, setJobGridColumns] = useState([])
	const [jobCurrentRecords, setJobCurrentRecords] = useState([])

	const parentFilters = useMemo(() => {
		if (isEmpty(jobGridColumns) || isEmpty(parentModalDashboardSetting)) return []

		const linkedFields = getChildLinkedFieldsByTileKey(tile?.key, parentModalDashboardSetting) || []

		const childLinkedFilters = linkedFields
			?.map((field, index) => {
				const column = jobGridColumns.find((col) => col.field === field)
				if (!column) return null

				const defaultOperator = getDefaultOperator(column.orgType, true)
				return {
					field,
					operator: parentPassedIdValue === '' ? 'isEmpty' : defaultOperator,
					value: parentPassedIdValue,
					type: column.type,
				}
			})
			.filter((item) => !!item)

		return childLinkedFilters
	}, [parentPassedIdValue, parentModalDashboardSetting, jobGridColumns, tile?.key])

	const filterModel = useMemo(() => {
		const filters = []
		if (searchText) {
			filters.push(searchText)
		}

		if (selectedJobStatusInfo && selectedJobStatusInfo.operator !== 'total') {
			filters.push(selectedJobStatusInfo)
		}

		return filters
	}, [searchText, selectedJobStatusInfo])

	// selected task grid section
	const {
		viewData: taskViewData,
		allColumns: taskAllColumns,
		serverFilter: taskServerFilter,
		viewLoading: taskViewLoading,
	} = useJobActivityViewQuery({
		formKey: selectedJobActivityForm?.formKey,
		projectKey: selectedJobActivityForm?.projectKey,
		columnsChecked,
		tile,
	})

	const [taskGridColumns, setTaskGridColumns] = useState([])
	const [taskCurrentRecords, setTaskCurrentRecords] = useState([])

	const { projectLoading } = useProject({ enabled: true })

	const tileLoading = useMemo(() => {
		return projectLoading || jobViewLoading
	}, [projectLoading, jobViewLoading])

	const [isLoading, setIsLoading] = useState(false)

	// for Job view
	useEffect(() => {
		if (jobViewLoading || !jobViewData) return

		// set the grid columns based on the view data
		async function setViewColumnsFromTileSettings() {
			await updateColumns({
				viewKey: jobViewData?.key,
				columnsArr: jobAllColumns,
				viewSession: true,
				token: environment?.apiToken,
			})

			setIsLoading(false)
		}
		try {
			setIsLoading(true)
			setViewColumnsFromTileSettings()
		} catch (error) {
			console.error('Error setting view columns:', error)
		}
	}, [jobViewData, columnsChecked, jobAllColumns, jobViewLoading])

	// for JobTask view
	useEffect(() => {
		if (taskViewLoading || !taskViewData) return

		// set the grid columns based on the view data
		async function setViewColumnsFromTileSettings() {
			await updateColumns({
				viewKey: taskViewData?.key,
				columnsArr: taskAllColumns,
				viewSession: true,
				token: environment?.apiToken,
			})

			setIsLoading(false)
		}
		try {
			setIsLoading(true)
			setViewColumnsFromTileSettings()
		} catch (error) {
			console.error('Error setting view columns:', error)
		}
	}, [taskViewData, taskAllColumns, taskViewLoading])

	const selectedJobFormQueryCondition = useMemo(() => {
		if (jobViewLoading || !jobViewData) return []
		const currentConditions = cloneDeep(jobViewData?.queries?.[0]?.filter?.conditions)
		const updatedConditions = currentConditions.map((condition) => {
			if (!COLUMNS_WITH_DEFAULT_PRESET_VALUE.includes(condition.target)) return condition

			delete condition['values']
			condition.preset = 'ALL'
			return condition
		})

		return updatedConditions
	}, [jobViewData, jobViewLoading])

	const { records, recordsLoading, refetchRecords } = useRecordQuery({
		viewKey: jobViewData?.key,
		viewType: jobViewData?.type,
		clientFilter: { conditions: selectedJobFormQueryCondition } ?? {},
		viewSession: true,
		isUpdateColumnsSession: isLoading,
	})

	const selectedColumns = useMemo(() => {
		if (!jobAllColumns?.length || !columnsChecked?.length) return []
		return jobAllColumns.filter((column) => columnsChecked.includes(column.name))
	}, [jobAllColumns, columnsChecked])

	useEffect(() => {
		const progressBarSettings =
			!showProgressBar && !showPercentage ? {} : { showProgressBar, showPercentage }
		const parsedGridColumns = parsedDataGridColumns(
			environment,
			selectedColumns,
			[statusField],
			jobStatusColors,
			[progressBarField],
			progressBarSettings
		)

		const formattedgridColumns = parsedGridColumns.map((column) => {
			if (DISPLAY_COLUMNS[column.field]) {
				return {
					...column,
					headerName: DISPLAY_COLUMNS[column.field],
				}
			}
			if (column.field !== jobIdField || isEmpty(jobIdFieldCaption)) return column
			return {
				...column,
				headerName: jobIdFieldCaption,
			}
		})

		if (isEqual(jobGridColumns, formattedgridColumns)) return
		setJobGridColumns(formattedgridColumns)
	}, [
		JSON.stringify(selectedColumns),
		jobIdField,
		jobIdFieldCaption,
		statusField,
		progressBarField,
		jobStatusColors,
		showProgressBar,
		showPercentage,
	])

	useEffect(() => {
		if (recordsLoading) return
		if (isEqual(records, jobCurrentRecords)) return
		setJobCurrentRecords(records)
	}, [recordsLoading, records])

	const currentRows = useMemo(() => {
		if (!jobCurrentRecords?.length || !jobAllColumns?.length) return []

		const allRows = getSelectedGridRecord({
			records: jobCurrentRecords,
			columns: jobAllColumns,
			environment,
			columnCheckList: jobAllColumns,
			allColumns: jobAllColumns,
		})

		const filters = [...jobServerFilter]
		if (!isEmpty(parentFilters)) {
			filters.push(...parentFilters)
		}

		const filteredRows = getFilteredRows(filters, allRows)

		return filteredRows
	}, [jobCurrentRecords, jobServerFilter, parentFilters, JSON.stringify(jobAllColumns)])

	const displayRows = useMemo(() => {
		return getFilteredRows(filterModel, currentRows)
	}, [currentRows, filterModel])

	const selectedJobTasksQueryCondition = useMemo(() => {
		if (taskViewLoading || !taskViewData) return []
		const currentConditions = cloneDeep(taskViewData?.queries?.[0]?.filter?.conditions)
		const updatedConditions = currentConditions.map((condition) => {
			if (!COLUMNS_WITH_DEFAULT_PRESET_VALUE.includes(condition.target)) return condition

			delete condition['values']
			condition.preset = 'ALL'
			return condition
		})

		return updatedConditions
	}, [taskViewData, taskViewLoading])

	const {
		records: taskRecords,
		recordsLoading: taskRecordsLoading,
		refetchRecords: refetchTaskRecords,
	} = useRecordQuery({
		viewKey: taskViewData?.key,
		viewType: taskViewData?.type,
		clientFilter: { conditions: selectedJobTasksQueryCondition } ?? {},
		viewSession: true,
		isUpdateColumnsSession: isLoading,
	})

	useEffect(() => {
		const parsedGridColumns = parsedDataGridColumns(
			environment,
			taskAllColumns,
			[taskStatusField],
			taskStatusColors
		)

		const formattedgridColumns = parsedGridColumns.map((column) => {
			if (DISPLAY_COLUMNS[column.field]) {
				return {
					...column,
					headerName: DISPLAY_COLUMNS[column.field],
				}
			}
			return column
		})

		if (isEqual(taskGridColumns, formattedgridColumns)) return
		setTaskGridColumns(formattedgridColumns)
	}, [JSON.stringify(taskAllColumns), taskStatusField, taskStatusColors])

	useEffect(() => {
		if (taskRecordsLoading) return
		if (isEqual(taskRecords, taskCurrentRecords)) return
		setTaskCurrentRecords(taskRecords)
	}, [taskRecordsLoading, taskRecords])

	const taskCurrentRows = useMemo(() => {
		if (!taskCurrentRecords?.length || !taskAllColumns?.length) return []

		const allRows = getSelectedGridRecord({
			records: taskCurrentRecords,
			columns: taskAllColumns,
			environment,
			columnCheckList: taskAllColumns,
			allColumns: taskAllColumns,
		})

		const filteredRows = getFilteredRows(taskServerFilter, allRows)
		return filteredRows
	}, [taskCurrentRecords, taskServerFilter, JSON.stringify(taskAllColumns)])

	const currentSelectedNewTaskForm = useMemo(() => {
		if (!selectedNewTaskForm || isEmpty(selectedNewTaskForm)) return selectedJobActivityForm
		return selectedNewTaskForm
	}, [selectedJobActivityForm, selectedNewTaskForm])

	const gridColumnsWithActionField = useMemo(() => {
		if (isEqual(jobGridColumns, [])) return []
		const actionField = {
			field: 'actions',
			type: 'actions',
			width: 75,
			disableReorder: true,
			getActions: (params) => {
				const loading = [
					<Box sx={{ pl: 1.4, position: 'relative', top: '3px', left: '-6px' }}>
						<CircularProgress color="inherit" size={17} />
					</Box>,
				]

				let popupActions = [
					<GridActionsCellItem
						key={params.id}
						icon={<AddIcon />}
						label={t('common:input.addTask')}
						onClick={() => {
							setPopulatedMessage([
								{
									field: jobIdField,
									value: params?.row?.[jobIdField] || '',
								},
							])
							handleOpenDialog()
						}}
						showInMenu
					/>,
					// *******WFM-480: Temporarily remove history action from action menu*******
					// <GridActionsCellItem
					// 	key={params.id}
					// 	icon={<AccessTimeIcon />}
					// 	label={t('common:misc.history')}
					// 	onClick={() => {
					// 		setActivityJobFieldValue(params.row?.[jobIdField] || '')
					// 		handleOpenDialog()
					// 	}}
					// 	showInMenu
					// />,
					<GridActionsCellItem
						key={params.id}
						icon={<VisibilityIcon />}
						label={t('common:misc.tasks')}
						onClick={() => {
							const currentJobIdColumn = params.columns?.find((col) => col.field === jobIdField)
							if (!currentJobIdColumn) {
								setError('Job Id field not found')
								return
							}

							const value = params.row[jobIdField] || ''
							const defaultOperator = getDefaultOperator(currentJobIdColumn.type)
							const jobIdValue = {
								field: jobIdField,
								operator: value === '' ? 'isEmpty' : defaultOperator,
								value,
								type: currentJobIdColumn.type,
								id: 'jobIdFilter',
							}
							setJobIdActionValue(jobIdValue)
							handleOpenDialog(true)
						}}
						showInMenu
					/>,
				]

				if (
					!environment.isReadOnlySubmission &&
					permissions &&
					hasUpdatingPermission(permissions)
				) {
					popupActions = [
						...popupActions,
						<GridActionsCellItem
							key={params.id}
							icon={<EditIcon />}
							label={t('common:misc.edit')}
							onClick={handleAction(params, 'edit', records, jobViewData)}
							showInMenu
						/>,
					]
				}

				if (!environment.isReadOnlySubmission && permissions && hasDeletePermission(permissions)) {
					popupActions = [
						...popupActions,
						<GridActionsCellItem
							key={params.id}
							icon={<DeleteIcon />}
							label={t('common:misc.delete')}
							onClick={handleAction(params, 'delete', records, jobViewData)}
							showInMenu
						/>,
					]
				}

				const allActions = [
					...(params?.row?.isLoading ? loading : []),
					...(!params?.row?.isLoading ? popupActions : []),
				]
				const AUTO_GENERATED_FOOTER_FROM_AGGREGATION = 'auto-generated-group-footer-root'

				// Remove the action menu icon from the aggregate row
				if (params?.id === AUTO_GENERATED_FOOTER_FROM_AGGREGATION) {
					return []
				}

				return allActions
			},
		}

		return [actionField, ...jobGridColumns]
	}, [environment, records, jobGridColumns])

	const intervalIdRef = useRef(null)

	function start() {
		if (intervalIdRef.current === null) {
			intervalIdRef.current = setTimeout(() => {
				refetchRecords()
				intervalIdRef.current = null
			}, 10000)
		}
	}

	function stop() {
		if (intervalIdRef.current !== null) {
			clearInterval(intervalIdRef.current)
			intervalIdRef.current = null
		}
	}

	function refetchAndStopTimeout() {
		refetchRecords()
		stop()
	}

	useEffect(() => {
		const customerKey = environment.account.key
		if (!customerKey) return
		const customerCountRef = ref(database, `userSessions/customerKey/${customerKey}`)
		const viewRef = ref(database, `userSessions/viewActivity/customerKey/${customerKey}`)

		const customerUnsubscribe = onValue(customerCountRef, (snapshot) => {
			// const data = snapshot.val()
			// const currentDateTime = moment().add(serverOffset, 'milliseconds').format('MM/DD/YYYY HH:mm:ss')

			// if (data && areDatesEqual(data?.Updated, currentDateTime, 'MM/DD/YYYY HH:mm:ss')) {
			sleep(2000).then(() => {
				refetchTaskRecords()
				refetchAndStopTimeout()
			})
			// }
		})

		const viewUnsubscribe = onValue(viewRef, (snapshot) => {
			sleep(2000).then(() => {
				refetchTaskRecords()
				refetchAndStopTimeout()
			})
		})

		return () => {
			off(customerCountRef)
			off(viewRef)

			customerUnsubscribe()
			viewUnsubscribe()
		}
	}, [environment.account.key])

	useEffect(() => {
		const formKey = selectedJobForm?.formKey
		const projectKey = selectedJobForm?.projectKey

		if (formKey && projectKey) {
			const formRef = ref(database, 'formSessions/' + projectKey + '/' + formKey)

			const unsubscribe = onValue(formRef, (snapshot) => {
				// Process data when there is a change
				sleep(2000).then(() => {
					refetchAndStopTimeout()
				})
			})

			// Returns the off function to turn off event listening when the component unmounts
			return () => {
				off(formRef)
				unsubscribe()
			}
		}
	}, [JSON.stringify(selectedJobActivityForm)])

	useEffect(() => {
		const formKey = selectedJobActivityForm?.formKey
		const projectKey = selectedJobActivityForm?.projectKey

		if (formKey && projectKey) {
			const formRef = ref(database, 'formSessions/' + projectKey + '/' + formKey)

			const unsubscribe = onValue(formRef, (snapshot) => {
				// Process data when there is a change
				refetchTaskRecords()
			})

			// Returns the off function to turn off event listening when the component unmounts
			return () => {
				off(formRef)
				unsubscribe()
			}
		}
	}, [JSON.stringify(selectedJobActivityForm)])

	const handleAction = useCallback(
		(params, action, records, viewData) => async () => {
			try {
				let record = records[params.id - 1]
				record = {
					...record,
					key:
						record.sourceType === 'SUBMISSION' || record.sourceType === 'ACTIVITY'
							? record.submissionKey
							: record.dispatchKey,
				}

				if (
					['edit', 'view', 'delete'].includes(action) &&
					('activityKey' in record || 'key' in record)
				) {
					/*var key = record.key ? record.key : record.activityKey;
						const response = await getFormSelectedFromActivityKey(
							record.key,
							environment.apiToken
						)
	
						const currentFormSelected = response.data;
						if (Object.keys(currentFormSelected).length > 0) {
							switch (tab) {
								case 'views':
									dispatch({
										type: VIEWS_ACTIONS.UPDATE_VIEW_SELECTED_KEY,
										payload: currentFormSelected.formKey,
									})
									break
								case 'devices':
									dispatch({
										type: DEVICES_ACTIONS.FORM_SELECTED,
										payload: currentFormSelected,
									})
									break
								case 'activity':
									dispatch({
										type: ACTIVITY_ACTIONS.FORM_SELECTED,
										payload: currentFormSelected,
									})
									break
								default:
									break
							}
							
						}*/
					setAction(action)
					setActionRecord(record)
					setOpen(action !== 'images' ? true : false)
				}
			} catch (err) {
				if (err?.response?.data?.code === 404) {
					setError(`${t('common:misc.record')} ${t('common:misc.hasBeenDeleted')}`)
				} else {
					setError('Code ' + err?.response?.data?.code + ': ' + err?.response?.data?.message)
				}
			}
		},
		[action, records]
	)

	return tileLoading ? (
		<LoadingSpinner />
	) : (
		<div className={classes.jobSummaryContainer}>
			<div ref={sideBarContainerRef} className={classes.sideBarContainer}>
				<div className={classes.summaryContainer}>
					{!isEmpty(summaryWidgets) && (
						<div style={{ marginBottom: '15px' }}>
							<Typography
								variant="body1"
								style={{ textAlign: 'start', fontWeight: 'bolder', marginLeft: '5px' }}
							>
								Job Status
							</Typography>
							{summaryWidgets?.map((widgetItem, index) => {
								const formatedWidgetItem = { ...widgetItem, field: statusField }
								return (
									<JobStatusCard
										widgetItem={formatedWidgetItem}
										selectedJobStatusInfo={selectedJobStatusInfo}
										allRows={currentRows}
										onActionAreaClick={() =>
											setSelectedJobStatusInfo((prev) =>
												prev?.id === widgetItem.id ? null : formatedWidgetItem
											)
										}
									/>
								)
							})}
						</div>
					)}
					{!isEmpty(taskSummaryWidgets) && (
						<div>
							<Typography
								variant="body1"
								style={{ textAlign: 'start', fontWeight: 'bolder', marginLeft: '5px' }}
							>
								Tasks Status
							</Typography>
							{taskSummaryWidgets?.map((widgetItem, index) => {
								const formatedWidgetItem = { ...widgetItem, field: taskStatusField }
								return (
									<JobStatusCard
										widgetItem={formatedWidgetItem}
										allRows={taskCurrentRows}
										onActionAreaClick={() => {
											setJobIdActionValue(formatedWidgetItem)
											handleOpenDialog(true)
										}}
									/>
								)
							})}
						</div>
					)}
				</div>
			</div>
			<div id="contentContainer" className={classes.contentContainer}>
				<div
					style={{
						marginRight: '5px',
						width: 'calc(100% - 5px)',
					}}
				>
					<JobSummaryWidget
						tile={tile}
						setSearchText={setSearchText}
						selectedNewJobForm={selectedNewJobForm}
						selectedJobForm={selectedJobForm}
						selectedCustomerForm={selectedCustomerForm}
						filterField={filterField}
						allColumns={jobAllColumns}
						startInterval={start}
					/>
				</div>

				<TilePaginationDatagrid
					datagridStyle={{
						marginLeft: '8px',
						maxHeight: `${tileWrapperHeight - 60}px`,
					}}
					isLoading={recordsLoading}
					tile={tile}
					rows={displayRows}
					columns={gridColumnsWithActionField}
					apiRef={apiRef}
					onCellClick={(params, event) => {
						if (params.field === 'actions') return

						const passedIdValue = params.row?.[mainModalDashboardSetting?.dataField]
						handleOpenModalDashboardDialog({
							passedIdValue,
							modalDashboardSetting: mainModalDashboardSetting,
						})
					}}
				/>

				<Dialog
					fullWidth={true}
					maxWidth="md"
					aria-labelledby="transition-modal-title"
					aria-describedby="transition-modal-description"
					className={classes.dialog}
					sx={{
						'& .MuiDialog-paper': {
							height: '90% !important',
						},
					}}
					open={openDialog}
					closeAfterTransition
				>
					<DialogTitle id="record-dialog-title">
						<div style={{ float: 'left', display: 'inline' }}>
							{isNullOrUndefined(activityJobFieldValue)
								? !isEmpty(populatedMessage)
									? t('common:input.addTask')
									: t('common:input.jobTasks')
								: t('common:misc.history')}
						</div>
						<div style={{ float: 'right', display: 'inline' }}>
							<IconButton onClick={(e) => handleCloseDialog()} alignItems="flex-end">
								<CloseIcon />
							</IconButton>
						</div>
					</DialogTitle>
					<DialogContent>
						{selectedJobForm &&
							selectedJobActivityForm &&
							!isNullOrUndefined(activityJobFieldValue) && (
								<JobActivityViewComponent
									tile={tile}
									selectedJobForm={selectedJobForm}
									selectedJobActivityForm={selectedJobActivityForm}
									activityJobFieldValue={activityJobFieldValue}
								/>
							)}
						{selectedJobActivityForm && jobIdActionValue && (
							<JobActivityGridComponent
								tile={tile}
								selectedJobActivityForm={selectedJobActivityForm}
								selectedJobIdSearch={jobIdActionValue}
								gridColumns={taskGridColumns}
								allRows={taskCurrentRows}
								refetchRecords={refetchTaskRecords}
								gridLoading={taskRecordsLoading}
							/>
						)}
						{currentSelectedNewTaskForm && !isEmpty(populatedMessage) && (
							<DoFormsFormTile
								tile={{ ...tile, isShowDialog: true }}
								openTileFrmGrid={true}
								initProjectFormInfo={currentSelectedNewTaskForm}
								setIsOpenDialog={handleCloseDialog}
								useSettings={false}
								populatedMessage={populatedMessage}
							/>
						)}
					</DialogContent>
				</Dialog>

				{action && (
					<DoformsRecordsActions
						formSelected={{
							...selectedJobForm,
							key: selectedJobForm?.formKey,
							name: selectedJobForm?.formName,
						}}
						setExternalError={setError}
						open={open}
						action={action}
						setAction={setAction}
						environment={environment}
						actionRecord={actionRecord}
						name={jobViewData.name}
						tab={'tile'}
						columns={jobGridColumns}
						onClose={handleClose}
						setGridRows={setJobCurrentRecords}
						refreshData={refetchRecords}
					/>
				)}

				{mainModalDashboardSetting && <ModalDashboard />}
			</div>
		</div>
	)
}

export function JobSummaryWidget(props) {
	const {
		tile,
		allColumns,
		filterField,
		setSearchText,
		selectedNewJobForm,
		selectedJobForm,
		selectedCustomerForm,
		startInterval,
	} = props
	const classes = useStyles()
	const [t] = useTranslation('common')
	const { environment } = useSelector((state) => state)

	const [currentText, setCurrentText] = useState('')
	const selectedColumnField = useMemo(
		() => allColumns?.find((column) => column.name === filterField),
		[allColumns, filterField]
	)

	// dialog section
	const [openDialog, setOpenDialog] = useState(false)
	const [titleLabel, setTitleLabel] = useState('')
	const [displayProjectFormInfo, setDisplayProjectFormInfo] = useState(null)

	const currentSelectNewJobForm = useMemo(() => {
		if (!selectedNewJobForm || isEmpty(selectedNewJobForm)) return selectedJobForm
		return selectedNewJobForm
	}, [selectedNewJobForm, selectedJobForm])

	function buildTitleLabel(projectFormInfo) {
		if (!projectFormInfo) return ''
		return projectFormInfo.projectName + ' / ' + projectFormInfo.formName
	}

	function handleOpenDialog(projectFormInfo) {
		setOpenDialog(true)
		setTitleLabel(buildTitleLabel(projectFormInfo))
		setDisplayProjectFormInfo(projectFormInfo)
	}

	function handleCloseDialog(shouldUseInterval = false) {
		setOpenDialog(false)
		setDisplayProjectFormInfo(null)
		if (startInterval && shouldUseInterval) {
			startInterval()
		}
	}

	function handleSearch(column, searchText) {
		if (!column) return

		if (isEmpty(searchText)) {
			setSearchText(null)
			return
		}

		const defaultOperator = getDefaultOperator(column.type)
		setSearchText({
			field: column.name,
			operator: defaultOperator,
			value: searchText,
			type: column.type,
			id: 'searchTextFilter',
		})
	}

	return (
		<div>
			<Grid className={classes.topNavContainer} spacing={1} container>
				<Grid xs md lg item>
					<Grid className={classes.topNavContainer} container>
						<Grid xs md lg item>
							<TextField
								id="search-input"
								sx={{
									mt: 0.5,
									mb: 0.5,
									pl: 1,
									pr: 1,
								}}
								value={currentText}
								onChange={(e) => setCurrentText(e.target.value)}
								onKeyDown={(event) => {
									if (event.key === 'Enter') {
										handleSearch(selectedColumnField, currentText)
									}
								}}
								InputLabelProps={{ shrink: false }}
								placeholder={t('common:misc.search')}
								size="small"
								fullWidth
							/>
						</Grid>
						<Grid xs={2} md={2} lg={2} item>
							<Button
								variant="outlined"
								onClick={(e) => {
									handleSearch(selectedColumnField, currentText)
								}}
								fullWidth
								startIcon={<FilterAltOutlinedIcon />}
							>
								{t('common:misc.filter')}
							</Button>
						</Grid>
					</Grid>
				</Grid>
				<Grid xs={2} md={2} lg={2} item>
					<Button
						variant="contained"
						onClick={() => handleOpenDialog(currentSelectNewJobForm)}
						fullWidth
					>
						+ {t('common:misc.newJob')}
					</Button>
				</Grid>
				<Grid xs={2} md={2} lg={2} item>
					<Button
						variant="contained"
						onClick={() => handleOpenDialog(selectedCustomerForm)}
						fullWidth
					>
						+ {t('common:misc.customer')}
					</Button>
				</Grid>
			</Grid>
			{displayProjectFormInfo && (
				<Dialog
					fullWidth={true}
					maxWidth="md"
					aria-labelledby="transition-modal-title"
					aria-describedby="transition-modal-description"
					className={classes.dialog}
					sx={{
						'& .MuiDialog-paper': {
							height: '90% !important',
						},
					}}
					open={openDialog}
					closeAfterTransition
				>
					<DialogTitle id="record-dialog-title">
						<div style={{ float: 'left', display: 'inline' }}>{titleLabel}</div>
						<div style={{ float: 'right', display: 'inline' }}>
							<IconButton onClick={(e) => handleCloseDialog()} alignItems="flex-end">
								<CloseIcon />
							</IconButton>
						</div>
					</DialogTitle>
					<DialogContent>
						{displayProjectFormInfo && (
							<DoFormsFormTile
								tile={{ ...tile, isShowDialog: true }}
								openTileFrmGrid={true}
								initProjectFormInfo={displayProjectFormInfo}
								setIsOpenDialog={handleCloseDialog}
								useSettings={false}
								handleChangeTitle={(projectFormInfo) => {
									const newTitleLabel = buildTitleLabel(projectFormInfo)
									if (isEqual(titleLabel, newTitleLabel)) return

									setTitleLabel(newTitleLabel)
								}}
							/>
						)}
					</DialogContent>
				</Dialog>
			)}
		</div>
	)
}
